import { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import CommonModal from "../../commonModals/CommonModal";
import CommonButton from "../../Buttons/CommonButton";
import {
  showInfoToast,
  showSuccessToast,
  showWarningToast,
} from "../../toastUtils";
import HandwritingCanvas from "./HandwritingCanvas";
import recognizeHandwriting from "./recognizeHandwriting";

const HandwritingPadModal = ({
  isOpen,
  onClose,
  onInsertText,
  onInsertImage,
}) => {
  const canvasRef = useRef(null);
  const [tool, setTool] = useState("pen");
  const [hasInk, setHasInk] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);

  const refreshInkState = useCallback(() => {
    setHasInk(!canvasRef.current?.isEmpty?.());
  }, []);

  const handleClear = () => {
    canvasRef.current?.clear();
    setHasInk(false);
  };

  const handleUndo = () => {
    canvasRef.current?.undo();
    refreshInkState();
  };

  const handleClose = () => {
    if (isRecognizing) return;
    handleClear();
    setTool("pen");
    onClose();
  };

  const handleInsertImage = () => {
    if (!hasInk) {
      showWarningToast("Please write something before inserting");
      return;
    }
    const dataUrl = canvasRef.current?.getDataUrl?.();
    if (!dataUrl) {
      showWarningToast("Could not capture handwriting");
      return;
    }
    onInsertImage?.(dataUrl);
    showSuccessToast("Handwriting image inserted");
    handleClose();
  };

  const handleInsertText = async () => {
    if (!hasInk) {
      showWarningToast("Please write something before inserting");
      return;
    }
    const dataUrl = canvasRef.current?.getDataUrl?.();
    if (!dataUrl) {
      showWarningToast("Could not capture handwriting");
      return;
    }

    setIsRecognizing(true);
    try {
      const text = await recognizeHandwriting(dataUrl);
      if (!text) {
        showInfoToast(
          "No text recognized. Try clearer writing, or insert as image instead."
        );
        return;
      }
      onInsertText?.(text);
      showSuccessToast("Handwriting converted to text");
      handleClear();
      setTool("pen");
      onClose();
    } catch (err) {
      console.error("Handwriting OCR failed:", err);
      showWarningToast("Text recognition failed. Try Insert as Image instead.");
    } finally {
      setIsRecognizing(false);
    }
  };

  const toolBtnClass = (active) =>
    `px-3 py-1.5 text-xs rounded-md border transition-colors ${
      active
        ? "border-[#217E90] bg-[#E9F5FE] text-[#217E90] font-semibold"
        : "border-gray-300 bg-white text-gray-600 hover:border-[#217E90]"
    }`;

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Write with e-pen / finger"
      size="lg"
      maxHeightVh={92}
      disableBackdropClose={isRecognizing}
      paperSx={{
        width: { xs: "calc(100vw - 12px)", sm: "min(92vw, 720px)" },
        maxWidth: { sm: "min(92vw, 720px)" },
      }}
      showCloseButton={!isRecognizing}
      actions={[
        {
          label: "Cancel",
          onClick: handleClose,
          variant: "reset-outlined",
          disabled: isRecognizing,
        },
        {
          label: isRecognizing ? "Recognizing…" : "Insert as Text",
          onClick: handleInsertText,
          variant: "reset-outlined",
          className:
            "bg-white text-[#217E90] border border-[#217E90] hover:bg-[#E9F5FE]",
          disabled: !hasInk || isRecognizing,
        },
        {
          label: "Insert as Image",
          onClick: handleInsertImage,
          variant: "primary-filled",
          disabled: !hasInk || isRecognizing,
        },
      ]}
    >
      <div className="space-y-3">
        <p className="text-xs text-gray-500">
          Write with stylus or finger. Insert as text (OCR) or keep as an image
          with the note.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={toolBtnClass(tool === "pen")}
            onClick={() => setTool("pen")}
            disabled={isRecognizing}
          >
            Pen
          </button>
          <button
            type="button"
            className={toolBtnClass(tool === "eraser")}
            onClick={() => setTool("eraser")}
            disabled={isRecognizing}
          >
            Eraser
          </button>
          <CommonButton
            label="Undo"
            type="button"
            size="sm"
            variant="reset-outlined"
            onClick={handleUndo}
            disabled={!hasInk || isRecognizing}
            className="text-xs"
          />
          <CommonButton
            label="Clear"
            type="button"
            size="sm"
            variant="danger-outline"
            onClick={handleClear}
            disabled={!hasInk || isRecognizing}
            className="text-xs"
          />
        </div>

        <div className="h-[min(50vh,360px)] min-h-[220px]">
          <HandwritingCanvas
            ref={canvasRef}
            tool={tool}
            onStrokeEnd={refreshInkState}
          />
        </div>

        {isRecognizing && (
          <p className="text-xs font-medium text-[#217E90] flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-[#217E90] animate-pulse" />
            Recognizing handwriting…
          </p>
        )}
      </div>
    </CommonModal>
  );
};

HandwritingPadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInsertText: PropTypes.func,
  onInsertImage: PropTypes.func,
};

export default HandwritingPadModal;
